/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable prefer-const */

import cx from 'classnames'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Cookies from 'universal-cookie'
import { isDesktop, isMobile, isNativeApp } from '../../client'
import { usePage } from '../../contexts'
import { IVideo } from '../../types'
import { Copyrights } from './Copyrights'
import { mode, playerConfig, subtitlesData, utils } from './Helpers'
import { Kaltura } from './Kaltura/Kaltura'
import {
  KalturaProps,
  LiveRefEntryKey,
  PlayerPropsModel,
} from './Models/PlayerModels'
import { PlayerContext } from './Store/PlayerContext'
import {
  DescriptionAndCredit,
  ImgWithRatio,
  StyledPlayerComp,
  VideoPlaceHolder,
  WrapStyledPlayerComp,
} from './Styles/Player.styles'
import { SubTitlesBtn } from './SubTitlesBtn/SubTitlesBtn'
import { mediaCdnReplace, prettyLog } from '../../utils'
import { useAdBlockStore, usePlayerStore, useRootStore } from '../../store'
import useGeoManifestFetch from './Helpers/useGeoManifestFetch'
import { imageSrcBlockAds } from '../../styles/GlobalVariables'

export const Player = (props: PlayerPropsModel) => {
  const playerTimeOut = useRef<NodeJS.Timeout | null>(null)
  const {
    video,
    img,
    pageMeta,
    fromParser = false,
    item,
    videoWithSubtitles,
    playerOnly,
  } = props

  let inChanel26 = false
  const page = usePage()
  const { liveEntries, sendPal } = page?.SiteMeta?.config?.player || {}
  const playerCtx = useContext(PlayerContext)
  const { playerStatePaused } = usePlayerStore()
  const { playerScript, isShowingMaavaron } = useRootStore()
  const [loadKalturaPlayer, setLoadKalturaPlayer] = useState(false)
  const [autoPlay, setAutoPlay] = useState<boolean>(false)
  const loadKantarConfig = playerConfig.fakeWindow.thirdParty.kantar
  const { setTargetPlayingNow } = playerCtx
  const cookies = new Cookies()
  const subTitlesOnCookie = cookies.get(
    subtitlesData.video_accessibility_cookie_name
  )
  const [userActive, setUserActive] = useState(false)
  const [subTitlesOn, setSubTitlesOn] = useState(subTitlesOnCookie === 'true')
  const [showSubTitleBtn, setShowSubTitleBtn] = useState(false)
  const imageCredit = page?.Content.Item?.image.credit
  const enable_kantar = useMemo(
    () => page?.SiteMeta?.config?.player?.kantar?.enable || false,
    [page?.SiteMeta?.config?.player?.kantar?.enable]
  )

  let { credit, description, duration, kalturaId, name, poster } =
    showSubTitleBtn && subTitlesOn && !utils.isLive(video.name, liveEntries)
      ? (videoWithSubtitles as IVideo)
      : video
  const { copyright, pageType, ads, levels } = pageMeta

  const descriptionAdCreditEl = useMemo(() => {
    const formattedImageCredit = fromParser
      ? ''
      : imageCredit
      ? `צילום תמונה ראשית: ${imageCredit}`
      : ''

    const str = [description, credit, formattedImageCredit]
      .filter(str => str && str !== '')
      .join(' | ')
    if (str === '') {
      return null
    }

    return <DescriptionAndCredit children={str} />
  }, [credit, description, fromParser, imageCredit])

  if (name) {
    name = name.trim()
  }

  const targetId = useMemo(() => {
    const randomNum = Math.floor(Math.random() * 1000)
    return ['kaltura-player', name, randomNum].filter(str => !!str).join('-')
  }, [name])

  useEffect(() => {
    if (fromParser) {
      setAutoPlay(false)
    } else {
      const configAutoPlay = page?.SiteMeta.config?.player?.autoplay
      if (!configAutoPlay) {
        setAutoPlay(true)
      } else {
        if (isDesktop() && configAutoPlay?.desktop !== false) {
          setAutoPlay(true)
        } else if (isNativeApp() && configAutoPlay?.app !== false) {
          setAutoPlay(true)
        } else if (isMobile() && configAutoPlay?.mobile !== false) {
          setAutoPlay(true)
        }
      }
    }
  }, [fromParser, page?.SiteMeta.config?.player?.autoplay])

  useEffect(() => {
    if (autoPlay) {
      setTargetPlayingNow(targetId, false)
    }
  }, [autoPlay, setTargetPlayingNow, targetId])

  useEffect(() => {
    if (
      (utils.isLive(name, liveEntries) && !inChanel26) ||
      videoWithSubtitles
    ) {
      setShowSubTitleBtn(true)
    }
  }, [
    inChanel26,
    liveEntries,
    name,
    setTargetPlayingNow,
    targetId,
    videoWithSubtitles,
  ])

  useEffect(() => {
    if (playerTimeOut.current) {
      clearTimeout(playerTimeOut.current)
    }

    if (playerScript.kalturaReady) {
      // if (!enable_kantar || playerScript.kantarReady) {
      if (
        page?.PageMeta.ads.block.dfp ||
        !isShowingMaavaron ||
        !isDesktop() ||
        page?.isPlayerEmbed
      ) {
        playerTimeOut.current = setTimeout(() => {
          setLoadKalturaPlayer(true)
        }, 250)
      }
      // }
    }

    return () => {
      playerTimeOut.current && clearTimeout(playerTimeOut.current)
    }
  }, [
    enable_kantar,
    isShowingMaavaron,
    page?.PageMeta.ads.block.dfp,
    page?.isPlayerEmbed,
    playerScript,
    playerScript.kalturaReady,
    playerScript.kantarReady,
  ])

  const _this = useMemo(
    () => ({
      defaultImages: playerConfig.fakeWindow.siteConfig.default_images,
      videPlaceHolder: <VideoPlaceHolder />,
    }),
    []
  )

  const subTitlesToggleHandler = useCallback(() => {
    const options = {
      path: '/',
      expires: new Date(
        new Date().getTime() +
          1000 * 60 * 60 * subtitlesData.video_accessibility_cookie_hour
      ),
    }
    cookies.set(
      subtitlesData.video_accessibility_cookie_name,
      !subTitlesOn,
      options
    )
    setSubTitlesOn(prevState => {
      return !prevState
    })
    window.location.reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTitlesOn, subtitlesData])

  // const { country } = useGeoPluginFetch()
  // AdBlock Decision Engine result (RESH13-1075): null = pending, 'block' / 'allowed'
  const adDecision = useAdBlockStore(state => state.decision)
  const country = useGeoManifestFetch()
  const blockRT = useMemo(
    () => !!ads.block?.dfp || !!ads.block?.player,
    [ads.block?.dfp, ads.block?.player]
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let kalturaIDParam: any, kalturaContentDuration: number
  if (kalturaId === null || kalturaId === undefined) {
    kalturaIDParam = -1
    kalturaContentDuration = -1
  } else {
    kalturaIDParam = kalturaId
    kalturaContentDuration = duration
  }

  // //Get Live Entry
  if (utils.isLive(name, liveEntries)) {
    const mobile = +(utils.isMobile() || playerConfig.inWebView)
    const isGlobal = +(country !== 'Israel')
    const withAccessibility = +subTitlesOn
    const entryRef = ('e' +
      mobile +
      isGlobal +
      withAccessibility) as LiveRefEntryKey
    const videoFetch = subTitlesOn ? name + '_captions' : name
    const entries =
      page?.SiteMeta?.config?.player?.liveEntries[videoFetch] ||
      playerConfig.fakeWindow.siteConfig.liveRefId[videoFetch]
    kalturaIDParam = entries ? entries.entries[entryRef] : ''
    inChanel26 = entries ? entries.mod === mode.BB : false
  }

  // AdBlock Decision Engine (RESH13-1075): show the block screen when blocked.
  // LIVE is never blocked - its mid-rolls are SSAI (server-side).
  const isLiveVideo = utils.isLive(name, liveEntries)

  //get kaltura image poster
  if (!poster && !fromParser && img) {
    if (img) {
      poster = img
    } else {
      const defaultImage = playerConfig.fakeWindow.siteConfig.default_images
      poster = utils.isNews()
        ? defaultImage.news.default_image
        : defaultImage.tv.default_image
    }
  }
  poster = mediaCdnReplace(poster)
  const title = fromParser
    ? video.description || ''
    : video.description || pageMeta.title

  // Memoized so the Kaltura setup effect (which tears down and rebuilds the
  // whole player when any of its props changes identity) doesn't re-fire on
  // every unrelated re-render of this component - without this, an object
  // literal here would give it a "new" item/levels reference every render.
  const Kaltura_Props: KalturaProps = useMemo(
    () => ({
      autoPlay,
      poster,
      kalturaId: kalturaIDParam,
      videoRef: name,
      name,
      contentDuration: kalturaContentDuration,
      inChanel26,
      fromParser,
      blockRT,
      pageType,
      country,
      loadKantarConfig,
      levels,
      targetId,
      defaultImages: _this.defaultImages,
      item,
      playerOnly,
      subTitlesOn,
      sendPal: sendPal !== 'off',
      title,
    }),
    [
      autoPlay,
      poster,
      kalturaIDParam,
      name,
      kalturaContentDuration,
      inChanel26,
      fromParser,
      blockRT,
      pageType,
      country,
      loadKantarConfig,
      levels,
      targetId,
      _this.defaultImages,
      item,
      playerOnly,
      subTitlesOn,
      sendPal,
      title,
    ]
  )

  if (copyright) {
    return <Copyrights src={mediaCdnReplace(copyright)} />
  }

  //set placehholder if failed to load video data
  if (
    (utils.isEmptyStr(kalturaId) && utils.isEmptyStr(name)) ||
    (utils.isLivePage(name) && utils.isEmptyStr(name))
  ) {
    prettyLog(
      'Player: 152',
      {
        msg: 'empty video',
        props: props,
      },
      'error'
    )
    return _this.videPlaceHolder
  }

  if (adDecision === 'block' && !isLiveVideo) {
    return (
      <ImgWithRatio
        loading='lazy'
        kalturaClass={'AdsBlockIMG'}
        src={mediaCdnReplace(imageSrcBlockAds)}
      />
    )
  }

  if (!!credit && !credit.includes('צילום')) {
    credit = `צילום: ${credit}`
  }

  const handelTouchEnd = () => {
    setUserActive(true)
    setTimeout(() => setUserActive(false), 3000)
  }

  return (
    <WrapStyledPlayerComp
      theme={{ fromParser: fromParser }}
      onMouseEnter={() => setUserActive(true)}
      onMouseLeave={() => setUserActive(false)}
      onTouchStart={handelTouchEnd}
      key={name}
    >
      <StyledPlayerComp
        className={cx([
          'player-comp ',
          utils.isLive(name, liveEntries) && 'live-player',
          fromParser && 'fromParser',
        ])}
        data-duration={kalturaContentDuration}
        theme={{
          poster: poster,
          inEmbed: page?.isPlayerEmbed,
          show: playerStatePaused,
        }}
        data-auto-play={autoPlay ? 'on' : 'off'}
      >
        {loadKalturaPlayer && (adDecision === 'allowed' || isLiveVideo) ? (
          <Kaltura {...Kaltura_Props} key={kalturaId} />
        ) : (
          ''
        )}
      </StyledPlayerComp>
      {showSubTitleBtn ? (
        <SubTitlesBtn
          toggle={subTitlesToggleHandler}
          subTitlesOn={subTitlesOn}
          theme={{
            userActive: userActive,
          }}
        />
      ) : (
        ''
      )}
      {descriptionAdCreditEl}
    </WrapStyledPlayerComp>
  )
}
export default Player
